/*
 * @package de.bistum-passau.www
 * @author Martin Hellwagner [martin@fredmansky.at]
 * navMainSubSite
 */

import _debounce from 'lodash/debounce';

const navMain = {
  cfg: {
    name: 'navMain',
    selectors: {
      body: 'body',
      navMain: '.m-navMain',
      linksMain: '.m-navMain__listItem--level-1',
      linksMainBtns: '.m-navMain__listItem--level-1 button',
      linksSub: '.m-navMain__list--level-2',
      linksSubLinks: '.m-navMain__listItem--level-2',
      linksSubMobile: '.m-navMain__list--level-2--mobile',
      burgerIcon: '.m-navMain__burgerIcon',
      closeIcon: '.m-navMain__closeIcon',
      menuMain: '.m-navMain__list',
      search: '.js-search .m-search__searchIcon',
    },

    classes: {
      active: 'is-active',
      visible: 'is-visible',
      visibleSub: 'is-visible-sub',
    },
    
    keys: {
      enter: 'Enter',
      space: ' ',
      escape: 'Escape',
      tab: 'Tab',
    },

    el: {
      $body: undefined,
      $navMain: undefined,
      $burgerIcon: undefined,
      $closeIcon: undefined,
      $searchIcon: undefined,
      $menuMain: undefined,
    },

    els: {
      $linkListMain: undefined,
      $linkListMainBtns: undefined,
      $linkListSub: undefined,
      $linkListSubLinks: undefined,
      $linkListSubMobile: undefined,
    },
    
    breakpoint: 1024,
    hoverDelay: 125,
    resizeDebounce: 250,
  },

  isDesktop() {
    return document.documentElement.clientWidth >= this.cfg.breakpoint;
  },

  isTouchDevice() {
    return 'ontouchstart' in window;
  },

  isKey(key, targetKey) {
    return key === targetKey;
  },

  isEnterOrSpace(key) {
    return this.isKey(key, this.cfg.keys.enter) || this.isKey(key, this.cfg.keys.space);
  },

  toggleClass(element, className, action = 'toggle') {
    if (element) {
      element.classList[action](className);
    }
  },

  setElements() {
    const sel = this.cfg.selectors;

    this.cfg.el.$body = document.querySelector(sel.body);
    this.cfg.el.$navMain = document.querySelector(sel.navMain);
    this.cfg.el.$burgerIcon = document.querySelector(sel.burgerIcon);
    this.cfg.el.$closeIcon = document.querySelector(sel.closeIcon);
    this.cfg.el.$searchIcon = document.querySelector(sel.search);
    this.cfg.el.$menuMain = document.querySelector(sel.menuMain);

    this.cfg.els.$linkListMain = document.querySelectorAll(sel.linksMain);
    this.cfg.els.$linkListMainBtns = document.querySelectorAll(sel.linksMainBtns);
    this.cfg.els.$linkListSub = document.querySelectorAll(sel.linksSub);
    this.cfg.els.$linkListSubLinks = document.querySelectorAll(sel.linksSubLinks);
    this.cfg.els.$linkListSubMobile = document.querySelectorAll(sel.linksSubMobile);
  },

  removeEventListenersFromElement(element, events) {
    if (!element) return;

    events.forEach(({ type, handler }) => {
      element.removeEventListener(type, handler);
    });
  },

  getDesktopEvents() {
    return [
      { type: 'mouseenter', handler: this.evtMouseEnter },
      { type: 'mouseleave', handler: this.evtMouseLeave },
    ];
  },

  getMobileEvents() {
    const events = [];

    if (this.isTouchDevice()) {
      events.push(
        { type: 'touchstart', handler: () => { this.cfg.touch = true; } },
        { type: 'touchmove', handler: () => { this.cfg.touch = false; } },
        { type: 'touchend', handler: this.evtFingerTouched }
      );
    } else {
      events.push({ type: 'click', handler: this.evtMouseClick });
    }

    return events;
  },

  addEventListener() {
    if (this.cfg.els.$linkListMain) {
      this.cfg.els.$linkListMain.forEach((link) => {
        const windowWidth = document.documentElement.clientWidth;

        if (windowWidth >= 1024) {
          link.addEventListener('mouseenter', this.evtMouseEnter);
          link.addEventListener('mouseleave', this.evtMouseLeave);

          const button = link.querySelector('button');
          if (button) {
            button.addEventListener('keydown', this.evtKeyHandler);
          }

          if ('ontouchstart' in window) {
            link.addEventListener('touchend', this.evtMouseClick);
          }
        } else if ('ontouchstart' in window) {
          link.addEventListener('touchstart', () => {
            this.cfg.touch = true;
          });
          link.addEventListener('touchmove', () => {
            this.cfg.touch = false;
          });
          link.addEventListener('touchend', this.evtFingerTouched);
        } else {
          link.addEventListener('click', this.evtMouseClick);
        }
      });
    }

    if (this.cfg.el.$searchIcon) {
      this.cfg.el.$searchIcon.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && e.shiftKey) {
          e.preventDefault();
          const lastButton = this.cfg.els.$linkListMainBtns[this.cfg.els.$linkListMainBtns.length - 1];
          lastButton?.focus();
        }
      });
    }

    if (this.cfg.el.$searchIcon && this.cfg.els.$linkListMainBtns.length) {
      const lastButton = this.cfg.els.$linkListMainBtns[this.cfg.els.$linkListMainBtns.length - 1];

      lastButton.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && !e.shiftKey) {
          e.preventDefault();
          this.cfg.el.$searchIcon.focus();
        }
      });
    }

    if (this.cfg.els.$linkListSub) {
      this.cfg.els.$linkListSub.forEach((link) => {
        const windowWidth = document.documentElement.clientWidth;

        if (windowWidth >= 1024) {
          link.addEventListener('mouseenter', this.evtMouseEnter);
          link.addEventListener('mouseleave', this.evtMouseLeave);

          if ('ontouchstart' in window) {
            link.addEventListener('touchend', this.evtMouseClick);
          }

          const submenuLinks = link.querySelectorAll('a');
          submenuLinks.forEach((link) => {
            link.addEventListener('keydown', this.evtKeyHandler);
          });
        }
      });
    }

    if (window.innerWidth < 1024 && this.cfg.els.$linkListMain) {
      this.cfg.els.$linkListMain.forEach((li) => {
        li.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            const button = li.querySelector('button[data-id]');
            if (!button) return;

            const submenuId = button.dataset.id;
            const submenu = document.getElementById(submenuId);
            if (!submenu) return;

            this.closeAllSubmenus();

            button.classList.add(this.cfg.classes.active);
            submenu.classList.add(this.cfg.classes.visible);

            const firstLink = submenu.querySelector('a, button, [tabindex]:not([tabindex="-1"])');
            firstLink?.focus();

            e.preventDefault();
          }
        });
      });
    }
  },

  removeHoverEventListener() {
    const allEvents = [
      ...this.getDesktopEvents(),
      ...this.getMobileEvents()
    ];

    this.cfg.els.$linkListMain?.forEach((link) => {
      this.removeEventListenersFromElement(link, allEvents);
    });

    this.cfg.els.$linkListSub?.forEach((link) => {
      this.removeEventListenersFromElement(link, this.getDesktopEvents());
    });
  },

  evtMouseEnter(evt) {
    if (evt.target.classList.contains(this.cfg.selectors.linksMain.substring(1))) {
      evt.target.classList.add(this.cfg.classes.active);

      const subNav = document.querySelector(`#${evt.target.dataset.id}`);
      if (subNav) {
        subNav.classList.add(this.cfg.classes.visible);
      }
    } else if (evt.target.classList.contains(this.cfg.selectors.linksSub.substring(1))) {
      evt.target.classList.add(this.cfg.classes.visibleSub);

      const mainNav = document.querySelector(`[data-id="${evt.target.id}"]`);
      if (mainNav) {
        mainNav.classList.add(this.cfg.classes.active);
      }
    }
  },

  evtMouseLeave(evt) {
    if (evt.target.classList.contains(this.cfg.selectors.linksMain.substring(1))) {
      evt.target.classList.remove(this.cfg.classes.active);

      const subNav = document.querySelector(`#${evt.target.dataset.id}`);
      if (subNav) {
        subNav.classList.remove(this.cfg.classes.visible);
      }
    } else if (evt.target.classList.contains(this.cfg.selectors.linksSub.substring(1))) {
      setTimeout(() => {
        evt.target.classList.remove(this.cfg.classes.visibleSub);

        const mainNav = document.querySelector(`[data-id="${evt.target.id}"]`);
        if (mainNav) {
          mainNav.classList.remove(this.cfg.classes.active);
        }
      }, this.cfg.hoverDelay);
    }
  },

  evtMouseClick(evt) {
    if (evt.target.classList.contains(this.cfg.selectors.linksMain.substring(1))) {
      evt.target.classList.toggle(this.cfg.classes.active);

      const subNav = this.isDesktop()
        ? document.querySelector(`[data-id="${evt.target.dataset.id}]`)
        : evt.target.querySelector(this.cfg.selectors.linksSubMobile);

      if (subNav) {
        subNav.classList.toggle(this.cfg.classes.visible);
      }
    } else if (evt.target.classList.contains(this.cfg.selectors.linksSub.substring(1))) {
      evt.target.classList.toggle(this.cfg.classes.visibleSub);

      const mainNav = document.querySelector(`[data-id="${evt.target.id}"]`);
      if (mainNav) {
        mainNav.classList.toggle(this.cfg.classes.active);
      }
    }
  },

  evtFingerTouched(evt) {
    if (this.cfg.touch) {
      evt.target.classList.toggle(this.cfg.classes.active);

      const submenu = evt.target.querySelector(this.cfg.selectors.linksSubMobile);
      if (submenu) {
        submenu.classList.toggle(this.cfg.classes.visible);
      }
    }
  },

  addEventListenerForMobileMenu() {
    this.cfg.el.$burgerIcon?.addEventListener('click', () => {
      this.openMobileMenu();
    });

    this.cfg.el.$closeIcon?.addEventListener('click', () => {
      this.closeMobileMenu();
    });

    this.cfg.el.$burgerIcon?.addEventListener('keydown', (e) => {
      if (this.isEnterOrSpace(e.key) && !this.isDesktop()) {
        e.preventDefault();
        this.openMobileMenu();
        const firstLevel1Button = this.cfg.els.$linkListMainBtns?.[0];
        if (firstLevel1Button) {
          setTimeout(() => firstLevel1Button.focus(), 0);
        }
      }
    });

    this.cfg.el.$closeIcon?.addEventListener('keydown', (e) => {
      if (this.isEnterOrSpace(e.key) && !this.isDesktop()) {
        e.preventDefault();
        this.closeMobileMenu();
      }
    });

    this.setupMobileFocusTrap();

    this.cfg.els.$linkListMainBtns?.forEach((btn) => {
      btn.addEventListener('keydown', (e) => {
        if (this.isEnterOrSpace(e.key)) {
          e.preventDefault();
          this.handleMobileSubmenuToggle(btn);
        }
      });
    });

    document.addEventListener('keydown', (e) => {
      if (this.isKey(e.key, this.cfg.keys.escape)) {
        this.handleMobileEscapeKey(e);
      }
    });
  },

  setupMobileFocusTrap() {
    const firstLevel1Btn = this.cfg.els.$linkListMainBtns?.[0];
    if (firstLevel1Btn) {
      firstLevel1Btn.addEventListener('keydown', (e) => {
        if (this.isKey(e.key, this.cfg.keys.tab) && e.shiftKey &&
          this.cfg.el.$menuMain?.classList.contains(this.cfg.classes.visible)
        ) {
          e.preventDefault();
          this.cfg.el.$closeIcon?.focus();
        }
      });
    }
  },

  handleMobileSubmenuToggle(btn) {
    const li = btn.closest(this.cfg.selectors.linksMain);
    const subMobile = li?.querySelector(this.cfg.selectors.linksSubMobile);
    if (!subMobile) return;

    subMobile.classList.toggle(this.cfg.classes.visible);
  },

  handleMobileEscapeKey(e) {
    const mobileOpen = this.cfg.el.$menuMain?.classList.contains(this.cfg.classes.visible);
    if (!mobileOpen) return;

    const activeSubMobile = document.querySelector(`${this.cfg.selectors.linksSubMobile}.${this.cfg.classes.visible}`);
    if (activeSubMobile && activeSubMobile.contains(document.activeElement)) {
      activeSubMobile.classList.remove(this.cfg.classes.visible);
      e.preventDefault();

      const parentLi = activeSubMobile.closest(this.cfg.selectors.linksMain);
      const parentButton = parentLi?.querySelector('button');
      if (parentButton) parentButton.focus();
    } else {
      this.closeMobileMenu();
      this.cfg.el.$burgerIcon?.focus();
      e.preventDefault();
    }
  },

  openMobileMenu() {
    this.toggleClass(this.cfg.el.$burgerIcon, this.cfg.classes.visible, 'remove');
    this.toggleClass(this.cfg.el.$closeIcon, this.cfg.classes.visible, 'add');
    this.toggleClass(this.cfg.el.$menuMain, this.cfg.classes.visible, 'add');

    if (this.cfg.el.$body) {
      this.cfg.el.$body.classList.add('mobileNavOpen');
    }
  },

  closeMobileMenu() {
    this.toggleClass(this.cfg.el.$burgerIcon, this.cfg.classes.visible, 'add');
    this.toggleClass(this.cfg.el.$closeIcon, this.cfg.classes.visible, 'remove');
    this.toggleClass(this.cfg.el.$menuMain, this.cfg.classes.visible, 'remove');

    const submenus = document.querySelectorAll(this.cfg.selectors.linksSubMobile);
    submenus.forEach(submenu => {
      submenu.classList.remove(this.cfg.classes.visible);
    });

    if (this.cfg.el.$body) {
      this.cfg.el.$body.classList.remove('mobileNavOpen');
    }
  },

  evtKeyHandler(evt) {
    const key = evt.key;
    const target = evt.currentTarget;

    const li = target.closest(this.cfg.selectors.linksMain);

    if ((key === 'Enter' || key === ' ') && li) {
      const submenuId = li.dataset.id;
      const submenu = document.getElementById(submenuId);
      if (!submenu) return;

      evt.preventDefault();

      const isOpen = li.classList.contains(this.cfg.classes.active);

      document.querySelectorAll(this.cfg.selectors.linksMain).forEach((el) => {
        el.classList.remove(this.cfg.classes.active);
      });
      document.querySelectorAll(this.cfg.selectors.linksSub).forEach((el) => {
        el.classList.remove(this.cfg.classes.visible);
      });

      if (!isOpen) {
        li.classList.add(this.cfg.classes.active);
        submenu.classList.add(this.cfg.classes.visible);
      }
      return;
    }

    if (key === 'Tab' && !evt.shiftKey) {
      const allLevel1Items = Array.from(document.querySelectorAll(this.cfg.selectors.linksMain));
      const index = allLevel1Items.indexOf(li);
      const isLastLevel1 = index === allLevel1Items.length - 1;

      if (isLastLevel1) {
        if (this.cfg.el.$searchIcon) {
          evt.preventDefault();
          this.cfg.el.$searchIcon.focus();
        }
      }

      const submenuContainer = target.closest(this.cfg.selectors.linksSub);
      if (submenuContainer) {
        const allLinks = submenuContainer.querySelectorAll('a');
        const isLast = target === allLinks[allLinks.length - 1];
        if (isLast) {
          evt.preventDefault();

          const submenuId = submenuContainer.id;
          const parentLi = document.querySelector(`${this.cfg.selectors.linksMain}[data-id="${submenuId}"]`);
          if (!parentLi) return;

          const allLevel1Items = Array.from(document.querySelectorAll(this.cfg.selectors.linksMain));
          const index = allLevel1Items.indexOf(parentLi);
          if (index >= 0 && index + 1 < allLevel1Items.length) {
            const nextLi = allLevel1Items[index + 1];
            const nextButton = nextLi.querySelector('button');
            if (nextButton) nextButton.focus();
          }
        }
      }

      if (li && li.classList.contains(this.cfg.classes.active)) {
        const submenu = document.getElementById(li.dataset.id);
        if (submenu) {
          const firstLink = submenu.querySelector('a');
          if (firstLink) {
            evt.preventDefault();
            firstLink.focus();
          }
        }
      }
      return;
    }

    if (key === 'Tab' && evt.shiftKey) {
      const submenuContainer = target.closest(this.cfg.selectors.linksSub);
      if (submenuContainer) {
        const allLinks = submenuContainer.querySelectorAll('a');
        const isFirst = target === allLinks[0];
        if (isFirst) {
          evt.preventDefault();
          const submenuId = submenuContainer.id;
          const parentLi = document.querySelector(`${this.cfg.selectors.linksMain}[data-id="${submenuId}"]`);
          if (parentLi) {
            const parentButton = parentLi.querySelector('button');
            if (parentButton) parentButton.focus();
          }
        }
      }
      return;
    }

    if (key === 'Escape') {
      const submenuContainer = target.closest(this.cfg.selectors.linksSub);
      if (submenuContainer) {
        const submenuId = submenuContainer.id;
        const parentLi = document.querySelector(`${this.cfg.selectors.linksMain}[data-id="${submenuId}"]`);
        const parentButton = parentLi?.querySelector('button');

        submenuContainer.classList.remove(this.cfg.classes.visible);
        parentLi?.classList.remove(this.cfg.classes.active);

        if (parentButton) {
          evt.preventDefault();
          parentButton.focus();
        }
      }

      if (li) {
        const submenu = document.getElementById(li.dataset.id);
        if (submenu && submenu.classList.contains(this.cfg.classes.visible)) {
          evt.preventDefault();
          li.classList.remove(this.cfg.classes.active);
          submenu.classList.remove(this.cfg.classes.visible);
        }
      }
    }
  },

  closeAllSubmenus() {
    const allButtons = document.querySelectorAll('[data-id]');
    const allSubmenus = document.querySelectorAll(`.${this.cfg.classes.visible}`);

    allButtons.forEach((btn) => btn.classList.remove(this.cfg.classes.active));
    allSubmenus.forEach((el) => el.classList.remove(this.cfg.classes.visible));
  },

  resize() {
    this.closeMobileMenu();
    this.removeHoverEventListener();
    this.addEventListener();
  },

  init() {
    this.evtMouseEnter = this.evtMouseEnter.bind(this);
    this.evtMouseLeave = this.evtMouseLeave.bind(this);
    this.evtMouseClick = this.evtMouseClick.bind(this);
    this.evtFingerTouched = this.evtFingerTouched.bind(this);
    this.evtKeyHandler = this.evtKeyHandler.bind(this);

    window.addEventListener('load', () => {
      this.setElements();
      this.addEventListener();
      if (!this.isDesktop()) {
        this.addEventListenerForMobileMenu();
      }
    });

    window.addEventListener('resize', _debounce(this.resize.bind(this), this.cfg.resizeDebounce));
  },
};

export default navMain;
